import { hasReadPermission } from "../hooks/usePermissionLockAndMessage";
import { sendGetActionTypes } from "./actionTypes";
import { sendGetKeyboardShortcuts } from "./keyboardShortcuts";
import { sendGetMatches } from "./matches";
import { sendGetMatchDetailedEvents } from "./matchEvents";
import { sendGetMatchTypes } from "./matchTypes";
import { sendGetTags } from "./tags";
import { sendGetTeams } from "./teams";
import { sendGetUsers, UserPermission } from "./users";
import { customFetch, HttpRequestMethod } from "./utils/send";

const isDatabaseHealthy = async () =>
  customFetch("/health-check/database", HttpRequestMethod.GET).then((result) => result.ok);

export const warmAuthorizers = async (userPermission?: UserPermission) => {
  console.log("Warming up authorizers...");
  const authWarmerPromises = [];
  if (hasReadPermission(userPermission)) {
    authWarmerPromises.push(customFetch("/warmers/read-authorizer", HttpRequestMethod.GET));
  }
  if (userPermission === UserPermission.track) {
    authWarmerPromises.push(customFetch("/warmers/track-authorizer", HttpRequestMethod.GET));
  }
  if (userPermission === UserPermission.write) {
    authWarmerPromises.push(customFetch("/warmers/write-authorizer", HttpRequestMethod.GET));
  }
  if (userPermission === UserPermission.owner) {
    authWarmerPromises.push(customFetch("/warmers/owner-authorizer", HttpRequestMethod.GET));
  }
  const authWarmers = await Promise.all(authWarmerPromises);
  if (authWarmers.every((result) => result.ok)) {
    console.log("Authorizers are warm.");
  }
};

export const warmLambdas = async () => {
  console.log("Warming up Lambdas...");
  const actionTypes = sendGetActionTypes();
  // The comment Lambda is shared by the database health check.
  const keyboardShortcuts = sendGetKeyboardShortcuts();
  const matchesAndEvents = sendGetMatches().then((matches) =>
    matches.length ? sendGetMatchDetailedEvents(matches[0].id) : null
  );
  // Match metric events are slow anyway, so a ~3.5s Lambda cold start is acceptable.
  const matchTypes = sendGetMatchTypes();
  const tags = sendGetTags();
  const teams = sendGetTeams();
  const users = sendGetUsers();

  await Promise.allSettled([
    actionTypes,
    keyboardShortcuts,
    matchesAndEvents,
    matchTypes,
    tags,
    teams,
    users,
  ]);

  console.log("Lambdas are warm.");
};

export const warmDatabase = async <T, U>(
  onDatabaseWarmSuccess?: () => Promise<T>,
  onDatabaseWarmFailure?: () => Promise<U>
): Promise<T | U | undefined> => {
  for (let i = 0; i < 30; i++) {
    console.log("Warming up database...");
    const databaseIsHealthy = await isDatabaseHealthy();

    if (databaseIsHealthy) {
      console.log("Database is warm.");
      return onDatabaseWarmSuccess ? onDatabaseWarmSuccess() : undefined;
    } else {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  return onDatabaseWarmFailure ? onDatabaseWarmFailure() : undefined;
};
